import { useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { Table, Heading, Flex, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import {
  cellValueUpdated,
  updateCostTrackerByCostItem as updateCostTrackerItem,
} from 'store/entities/cost-tracker/cost-tracker';
import translations from 'src/internationalisation/translation-map.json';
import { buildHeaders, buildCostTrackerItem } from 'views/cost-tracker/cost-tracker-common/operations-table-builder';
import { selectDisabledFields } from 'store/entities/cost-tracker/selectors';
import { selectCostItemList } from 'store/entities/cost-setup/selector';
import { GroupedCostTrackerByCostItem, GroupedCostTrackerItem } from 'src/common/interfaces/cost-tracker.interfaces';
import type { TRootState } from 'src/store/store-types';
import { getSetionData } from './utils/utils';
import { getColumnAlignment } from './utils/get-ct-column-alignment';
import { getColumnWidth } from './utils/get-ct-column-width';
import { EditColumns } from './edit-columns';

type TOperationsTableProps = {
  itemsList: GroupedCostTrackerItem[];
  itemsByCostList: GroupedCostTrackerByCostItem;
  disabledFields: string[];
  isPageDisabled: boolean;
  costItems: any[];
  primaryCurrency: string;
} & PropsFromRedux;
const OperationsTable = ({
  itemsList,
  itemsByCostList,
  disabledFields,
  cellValueUpdated,
  updateCostTrackerItem,
  primaryCurrency,
}: TOperationsTableProps) => {
  const { t } = useTranslation();
  const debounceUpdateTask = useRef(debounce(updateCostTrackerItem, 500));
  const rows: any[] = [];

  const [selectedRowId, setSelectedRowId] = useState('');
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<string[]>([]);
  const [collapsedOperationIds, setCollapsedOperationIds] = useState<string[]>([]);

  const toggleSelectedRow = (sectionId: string) =>
    selectedRowId === sectionId ? setSelectedRowId('') : setSelectedRowId(sectionId);

  const toggleSectionRow = (sectionId: string) =>
    collapsedSectionIds.includes(sectionId)
      ? setCollapsedSectionIds(collapsedSectionIds.filter((id) => id !== sectionId))
      : setCollapsedSectionIds([...collapsedSectionIds, sectionId]);

  const toggleOperationRow = (operationId: string) =>
    collapsedOperationIds.includes(operationId)
      ? setCollapsedOperationIds(collapsedOperationIds.filter((id) => id !== operationId))
      : setCollapsedOperationIds([...collapsedOperationIds, operationId]);

  itemsList?.forEach((section) => {
    const isSectionExpanded = !collapsedSectionIds.includes(section.sectionId);
    rows.push({
      key: section.sectionId,
      noDrag: true,
      cells: buildCostTrackerItem({
        data: getSetionData(section),
        disabledFields,
        isOperation: false,
        isExpanded: isSectionExpanded,
      }),
      onRowClick: () => toggleSectionRow(section.sectionId),
    });

    section.operations.forEach((operation) => {
      const isOperationExpanded = !collapsedOperationIds.includes(operation.originId);

      if (isSectionExpanded) {
        rows.push({
          key: operation.costTrackerItemId,
          costTrackerItemId: operation.costTrackerItemId,
          sectionId: operation.sectionId,
          active: selectedRowId === operation.costTrackerItemId,
          onRowClick: () => toggleOperationRow(operation.originId),
          cells: buildCostTrackerItem({
            data: operation,
            disabledFields,
            isOperation: false,
            cellValueUpdated,
            updateTask: debounceUpdateTask,
            isExpanded: isOperationExpanded,
          }),
        });
      }
      itemsByCostList[operation.originId]?.forEach((costItem: any) => {
        if (isOperationExpanded && isSectionExpanded) {
          rows.push({
            key: costItem.costTrackerItemId,
            costTrackerItemId: costItem.costTrackerItemId,
            sectionId: operation.sectionId,
            active: selectedRowId === costItem.costTrackerItemId,
            onRowClick: () => toggleSelectedRow(costItem.costTrackerItemId),
            cells: buildCostTrackerItem({
              data: { ...costItem, sectionId: operation.sectionId },
              disabledFields,
              isOperation: true,
              cellValueUpdated,
              updateTask: debounceUpdateTask,
            }),
            actions: [
              {
                subActions: [
                  {
                    icon: 'add above',
                    label: t(translations.timeTracker_addTaskAbove),
                    // onClick: () =>
                    //   addCostTrackerItem({
                    //     insertType: addTaskOptions.ABOVE,
                    //     costTrackerItemId: operation.costTrackerItemId,
                    //     projectId,
                    //   }),
                    disabled: true,
                  },
                  {
                    icon: 'add below',
                    label: t(translations.timeTracker_addTaskBelow),
                    // onClick: () =>
                    //   addCostTrackerItem({
                    //     insertType: addTaskOptions.BELOW,
                    //     costTrackerItemId: operation.costTrackerItemId,
                    //     projectId,
                    //   }),
                    disabled: true,
                  },
                  {
                    icon: 'minus',
                    label: t(translations.delete),
                    // onClick: () =>
                    //   deleteCostTrackerItem(
                    //     projectId,
                    //     operation.costTrackerId,
                    //     operation.costTrackerItemId,
                    //   ),
                    disabled: true,
                  },
                ],
              },
            ],
          });
        }
      });
    });
  });

  const tableWithoutData = {
    actions: [
      {
        icon: 'upload',
        tooltip: t(translations.timeTracker_upload),
        onClick: () => null,
        disabled: true,
      },
    ],
    name: t(translations.costTracker_costTracker),
    actionsRight: true,
  };

  const table = {
    testId: 'cost-tracker-table',
    maxHeight: 'calc(100% - 16px)',
    actions: [
      {
        icon: 'download',
        tooltip: t(translations.timeTracker_download),
        onClick: () => null,
        disabled: true,
      },
      {
        icon: 'upload',
        tooltip: t(translations.timeTracker_upload),
        onClick: () => null,
        disabled: true,
      },
      {
        childComponent: <EditColumns />,
      },
    ],
    name: t(translations.costTracker_costTracker),
    actionsRight: true,
    columnWidths: getColumnWidth(disabledFields),
    columnAlignment: getColumnAlignment(disabledFields),
    headers: buildHeaders(disabledFields, primaryCurrency),
    rows,
  };

  return itemsList?.length ? (
    <div style={{ maxHeight: 'calc(100% - 93px)', height: '100%' }}>
      <Table table={table} />
    </div>
  ) : (
    <>
      <Table table={tableWithoutData} />
      <Flex alignItems="center" justifyContent="center" height="calc(100% - 127px)">
        <Heading top>{t(translations.timeTracker_noOperationsFound)}</Heading>
      </Flex>
    </>
  );
};

const mapStateToProps = (state: TRootState) => {
  return {
    disabledFields: selectDisabledFields(state.entities),
    costItems: selectCostItemList(state),
  };
};

const mapDispatchToProps = {
  cellValueUpdated,
  updateCostTrackerItem,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
const Container = withErrorBoundary(connector(OperationsTable));

export { Container as OperationsTable };
